// ショートカットキーの案内を表示済みかどうか、ブラウザへ保存するユーティリティ。
// 連続してツールを選んだ回数はページ内だけの状態とし、ここでは案内の進み具合だけを扱う。

const TOOL_SHORTCUT_KEY = "refinement-canvas:hint:tool-shortcut-shown";
/**
 * ツール 1 個ぶんの段階を保存するキーの接頭辞。実際のキーは
 * `refinement-canvas:hint:tool-shortcut-stage:place-sticky` のようにツール名を連結した形になる。
 *
 * 全ツールの段階を 1 個のキーへまとめて持つと、保存が「読み込み → 書き換え → 書き戻し」になる。
 * 別のタブが同じ旧値を読んだあとにそれぞれ全体を書き戻すと、後から書いた側だけが残り、
 * 相手のタブが進めた別ツールの段階が丸ごと消える（そのツールは次回の読み込みで未案内へ戻る）。
 * ツールごとに独立したキーへ 1 回の `setItem` で書けば、書き込みが他ツールの記録へ触れないため、
 * この取りこぼしが起こらない。
 */
const TOOL_SHORTCUT_STAGE_KEY_PREFIX = "refinement-canvas:hint:tool-shortcut-stage:";
const PEN_SHIFT_KEY = "refinement-canvas:hint:pen-shift-shown";
const DOUBLE_CLICK_PLACE_KEY = "refinement-canvas:hint:double-click-place-shown";

/** ツール案内の段階。0=未案内、1=1 回案内済み、2=2 回案内済み、3=打ち止め。 */
export type ToolShortcutHintStage = number;

/**
 * 段階の上限（打ち止め）。案内の回数は 3 回までであり、これを超える値は丸める。
 * 閾値の本数（use-shortcut-hint-notices の TOOL_SHORTCUT_HINT_THRESHOLDS）と同じ値だが、
 * 保存値の健全化はここで完結させたいため、依存を作らず定数として持つ。
 */
const MAX_TOOL_SHORTCUT_HINT_STAGE = 3;

/** 案内済みのツール名を読み込む。読み込めない場合は未表示として扱う。 */
export function loadShownToolShortcutHints(): Set<string> {
  try {
    if (typeof localStorage === "undefined") return new Set();
    const raw = localStorage.getItem(TOOL_SHORTCUT_KEY);
    if (!raw) return new Set();
    return new Set(raw.split(",").filter((tool) => tool.length > 0));
  } catch {
    return new Set();
  }
}

/** 段階を 0 以上・上限以下の整数へ丸める。 */
function clampStage(stage: number): ToolShortcutHintStage {
  if (stage < 0) return 0;
  if (stage > MAX_TOOL_SHORTCUT_HINT_STAGE) return MAX_TOOL_SHORTCUT_HINT_STAGE;
  return stage;
}

/** 保存値 1 個を段階へ解析する。整数として読めない値は未案内（段階 0）として扱う。 */
function parseStoredStage(raw: string | null): ToolShortcutHintStage {
  // Number は空文字と null を 0 にするため、整数判定より先に除く。
  if (raw === null || raw.length === 0) return 0;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed)) return 0;
  return clampStage(parsed);
}

/** ツールごとの案内段階を読み込む。旧キーの表示済みツールは段階 1 として引き継ぐ。 */
export function loadToolShortcutHintStages(): Map<string, ToolShortcutHintStage> {
  try {
    if (typeof localStorage === "undefined") return new Map();
    const stages = new Map<string, ToolShortcutHintStage>();
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key === null || !key.startsWith(TOOL_SHORTCUT_STAGE_KEY_PREFIX)) continue;
      const tool = key.slice(TOOL_SHORTCUT_STAGE_KEY_PREFIX.length);
      if (tool.length === 0) continue;
      const stage = parseStoredStage(localStorage.getItem(key));
      // 段階 0（負数を丸めた値と壊れた値を含む）は「未案内」であり、記録が無いのと同じ意味なので
      // 保持しない。保持すると、このあとの旧キーからの移行を妨げてしまう。
      if (stage === 0) continue;
      stages.set(tool, stage);
    }
    // 旧仕様は「3 回連続で 1 回だけ案内」だったため、旧キーにあるツールは 1 回案内済み＝段階 1 とみなす。
    // 新キーにある値のほうが新しいので、段階 2・3 が段階 1 へ巻き戻らないよう新キーを優先する。
    for (const tool of loadShownToolShortcutHints()) {
      if (!stages.has(tool)) stages.set(tool, 1);
    }
    return stages;
  } catch {
    return new Map();
  }
}

/**
 * 1 ツールの段階を保存する（そのツール専用のキーだけを書き、旧キーは読み取り専用のまま残す）。
 * 段階は進む一方の値なので、保存済みより小さい値では上書きしない。別タブが先に打ち止め（段階 3）
 * まで進めていても、古い段階を持ったままのタブが書き戻して案内を復活させないための不可逆化である。
 */
export function saveToolShortcutHintStage(tool: string, stage: ToolShortcutHintStage): void {
  try {
    if (typeof localStorage === "undefined") return;
    const key = `${TOOL_SHORTCUT_STAGE_KEY_PREFIX}${tool}`;
    // 別タブで先に進められた段階を上書きで失わないよう、書込み直前に同じキーを読み直す。
    // 読み書きは対象ツールのキー 1 個に閉じるため、他ツールの段階へは触れない。
    const savedStage = parseStoredStage(localStorage.getItem(key));
    localStorage.setItem(key, String(clampStage(Math.max(savedStage, stage))));
  } catch {
    // 保存できない場合は無視する。案内自体は現在のページ内で段階どおりに表示される。
  }
}

/** ペンの Shift 直線案内を表示済みかどうか読み込む。 */
export function loadPenShiftHintShown(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(PEN_SHIFT_KEY) === "1";
  } catch {
    return false;
  }
}

/** ペンの Shift 直線案内を表示済みとして保存する。 */
export function markPenShiftHintShown(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(PEN_SHIFT_KEY, "1");
  } catch {
    // 保存できない場合は無視する。案内自体は現在のページ内で一度だけ表示される。
  }
}

/** ダブルクリック配置の案内を表示済みかどうか読み込む。 */
export function loadDoubleClickPlaceHintShown(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(DOUBLE_CLICK_PLACE_KEY) === "1";
  } catch {
    return false;
  }
}

/** ダブルクリック配置の案内を表示済みとして保存する。 */
export function markDoubleClickPlaceHintShown(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(DOUBLE_CLICK_PLACE_KEY, "1");
  } catch {
    // 保存できない場合は無視する。案内自体は現在のページ内で一度だけ表示される。
  }
}
